/**
 * User Preferences & Taste Profile
 * This is YOUR personalized entertainment DNA
 */

export interface FavoriteMedia {
  title: string;
  tmdbId?: number;
  year?: number;
  addedAt: number;
  rating?: number; // 1-10
  notes?: string;
}

export interface UserProfile {
  id: string;
  createdAt: number;
  updatedAt: number;

  // Sports Preferences
  sports: {
    favoriteTeams: string[];
    favoriteLeagues: string[];
    timezone: string;
  };

  // Entertainment Preferences
  favorites: {
    movies: FavoriteMedia[];
    tvShows: FavoriteMedia[];
    anime: FavoriteMedia[];
    franchises: string[];
    genres: string[];
    networks: string[];
    eras: string[];
  };

  // Negative Preferences (things to avoid)
  dislikes: {
    movies: string[];
    franchises: string[];
    genres: string[];
  };

  // Taste Profile (learned over time)
  tasteProfile: {
    likedGenres: Record<string, number>;
    dislikedGenres: Record<string, number>;
    avgRating: number;
    totalWatchTime: number;
  };
}

// Your initial preferences based on what you told me
export const DEFAULT_USER_PROFILE: UserProfile = {
  id: 'default-user',
  createdAt: Date.now(),
  updatedAt: Date.now(),

  sports: {
    favoriteTeams: ['Los Angeles Lakers', 'San Antonio Spurs'],
    favoriteLeagues: ['NBA'],
    timezone: 'America/New_York', // EST
  },

  favorites: {
    movies: [
      // Star Wars (Episodes 1-6 + Rogue One) - ROTS is #1!
      { title: 'Star Wars: Episode III - Revenge of the Sith', tmdbId: 1895, year: 2005, addedAt: Date.now(), rating: 10, notes: 'GOAT Star Wars film' },
      { title: 'Star Wars: Episode V - The Empire Strikes Back', tmdbId: 1891, year: 1980, addedAt: Date.now(), rating: 10 },
      { title: 'Star Wars: Episode IV - A New Hope', tmdbId: 11, year: 1977, addedAt: Date.now(), rating: 10 },
      { title: 'Star Wars: Episode VI - Return of the Jedi', tmdbId: 1892, year: 1983, addedAt: Date.now(), rating: 9 },
      { title: 'Star Wars: Episode I - The Phantom Menace', tmdbId: 1893, year: 1999, addedAt: Date.now(), rating: 8 },
      { title: 'Star Wars: Episode II - Attack of the Clones', tmdbId: 1894, year: 2002, addedAt: Date.now(), rating: 8 },
      { title: 'Rogue One: A Star Wars Story', tmdbId: 330459, year: 2016, addedAt: Date.now(), rating: 9 },

      // Mind-bending Sci-Fi
      { title: 'Inception', tmdbId: 27205, year: 2010, addedAt: Date.now(), rating: 10 },
      { title: 'Interstellar', tmdbId: 157336, year: 2014, addedAt: Date.now(), rating: 10 },

      // Matrix Trilogy (1, 2, 3 ONLY)
      { title: 'The Matrix', tmdbId: 603, year: 1999, addedAt: Date.now(), rating: 10 },
      { title: 'The Matrix Reloaded', tmdbId: 604, year: 2003, addedAt: Date.now(), rating: 9 },
      { title: 'The Matrix Revolutions', tmdbId: 605, year: 2003, addedAt: Date.now(), rating: 8 },

      // Classics
      { title: 'Forrest Gump', tmdbId: 13, year: 1994, addedAt: Date.now(), rating: 10 },

      // Marvel (Pre-Endgame) - Add more as needed
      { title: 'Avengers: Endgame', tmdbId: 299536, year: 2019, addedAt: Date.now(), rating: 10, notes: 'Last good Marvel movie' },
      { title: 'Avengers: Infinity War', tmdbId: 299534, year: 2018, addedAt: Date.now(), rating: 10 },
      { title: 'Iron Man', tmdbId: 1726, year: 2008, addedAt: Date.now(), rating: 9 },
      { title: 'The Avengers', tmdbId: 24428, year: 2012, addedAt: Date.now(), rating: 9 },
      { title: 'Captain America: The Winter Soldier', tmdbId: 100402, year: 2014, addedAt: Date.now(), rating: 9 },
      { title: 'Guardians of the Galaxy', tmdbId: 118340, year: 2014, addedAt: Date.now(), rating: 9 },
      { title: 'Thor: Ragnarok', tmdbId: 284053, year: 2017, addedAt: Date.now(), rating: 9 },
      { title: 'Black Panther', tmdbId: 284054, year: 2018, addedAt: Date.now(), rating: 9 },
    ],

    tvShows: [
      // Star Wars Shows
      { title: 'The Mandalorian', tmdbId: 82856, year: 2019, addedAt: Date.now(), rating: 10 },
      { title: 'Star Wars: The Clone Wars', tmdbId: 4194, year: 2008, addedAt: Date.now(), rating: 10 },

      // Netflix Reality/Drama
      { title: 'Love Is Blind', tmdbId: 93968, year: 2020, addedAt: Date.now(), rating: 8 },
      { title: 'Squid Game', tmdbId: 93405, year: 2021, addedAt: Date.now(), rating: 9 },

      // DC Shows
      { title: 'The Flash', tmdbId: 60735, year: 2014, addedAt: Date.now(), rating: 8 },
      { title: 'Arrow', tmdbId: 1412, year: 2012, addedAt: Date.now(), rating: 8 },
    ],

    anime: [
      // THE GOAT
      { title: 'One Piece', tmdbId: 37854, year: 1999, addedAt: Date.now(), rating: 10, notes: 'Best anime ever created' },
    ],

    franchises: [
      'Star Wars (Episodes 1-6, Rogue One, Mandalorian, Clone Wars)',
      'Marvel Cinematic Universe (up to Endgame)',
      'Matrix Trilogy (1, 2, 3)',
      'DC Universe',
      'Disney Classics',
      'Christopher Nolan Films',
    ],

    genres: [
      'Science Fiction',
      'Action',
      'Adventure',
      'Animation',
      'Anime',
      'Superhero',
      'Space Opera',
      'Thriller',
      'Drama',
      'Reality TV',
    ],

    networks: [
      'Cartoon Network',
      'Nickelodeon',
      'MTV',
      'BET',
      'Zeus Network',
      'Disney',
      'Netflix',
      'Disney+',
    ],

    eras: [
      '1995-2010', // Your golden era!
    ],
  },

  dislikes: {
    movies: [
      'The Matrix Resurrections', // Matrix 4 - not canon
    ],
    franchises: [
      'Marvel Phase 4+', // Post-Endgame Marvel
      'Star Wars Sequels', // Episodes 7, 8, 9
    ],
    genres: [],
  },

  tasteProfile: {
    likedGenres: {
      'Science Fiction': 10,
      'Action': 9,
      'Adventure': 9,
      'Animation': 9,
      'Anime': 10,
      'Superhero': 8,
      'Space Opera': 10,
      'Thriller': 8,
      'Drama': 7,
      'Reality TV': 7,
    },
    dislikedGenres: {},
    avgRating: 8.5,
    totalWatchTime: 0,
  },
};

// Storage key
const STORAGE_KEY = 'cinevault_user_profile';

// Load user profile from localStorage
export function loadUserProfile(): UserProfile {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('Error loading user profile:', error);
  }
  return DEFAULT_USER_PROFILE;
}

// Save user profile to localStorage
export function saveUserProfile(profile: UserProfile): void {
  try {
    const updated = { ...profile, updatedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Error saving user profile:', error);
  }
}

// Add a favorite movie
export function addFavoriteMovie(movie: FavoriteMedia): UserProfile {
  const profile = loadUserProfile();
  if (profile.favorites.movies.find(m => m.title === movie.title)) {
    return profile;
  }
  const updated: UserProfile = {
    ...profile,
    favorites: {
      ...profile.favorites,
      movies: [...profile.favorites.movies, { ...movie, addedAt: Date.now() }],
    },
  };
  saveUserProfile(updated);
  return updated;
}

// Add a favorite TV show
export function addFavoriteTvShow(show: FavoriteMedia): UserProfile {
  const profile = loadUserProfile();
  if (profile.favorites.tvShows.find(s => s.title === show.title)) {
    return profile;
  }
  const updated: UserProfile = {
    ...profile,
    favorites: {
      ...profile.favorites,
      tvShows: [...profile.favorites.tvShows, { ...show, addedAt: Date.now() }],
    },
  };
  saveUserProfile(updated);
  return updated;
}

// Add a favorite anime
export function addFavoriteAnime(anime: FavoriteMedia): UserProfile {
  const profile = loadUserProfile();
  if (profile.favorites.anime.find(a => a.title === anime.title)) {
    return profile;
  }
  const updated: UserProfile = {
    ...profile,
    favorites: {
      ...profile.favorites,
      anime: [...profile.favorites.anime, { ...anime, addedAt: Date.now() }],
    },
  };
  saveUserProfile(updated);
  return updated;
}

// Update taste profile based on watched content
export function updateTasteProfile(
  genre: string,
  liked: boolean,
  watchTime: number
): UserProfile {
  const profile = loadUserProfile();

  const updatedGenres = liked
    ? { ...profile.tasteProfile.likedGenres, [genre]: (profile.tasteProfile.likedGenres[genre] || 0) + 1 }
    : profile.tasteProfile.likedGenres;
  const updatedDisliked = !liked
    ? { ...profile.tasteProfile.dislikedGenres, [genre]: (profile.tasteProfile.dislikedGenres[genre] || 0) + 1 }
    : profile.tasteProfile.dislikedGenres;

  const updated: UserProfile = {
    ...profile,
    tasteProfile: {
      ...profile.tasteProfile,
      likedGenres: updatedGenres,
      dislikedGenres: updatedDisliked,
      totalWatchTime: profile.tasteProfile.totalWatchTime + watchTime,
    },
  };
  saveUserProfile(updated);
  return updated;
}

// Get AI context string for Gemini
export function getAIContextString(profile: UserProfile): string {
  return `
User Profile Summary:
- Favorite Teams: ${profile.sports.favoriteTeams.join(', ')}
- Favorite Franchises: ${profile.favorites.franchises.join(', ')}
- Favorite Genres: ${profile.favorites.genres.join(', ')}
- Favorite Era: ${profile.favorites.eras.join(', ')}
- Top Movies: ${profile.favorites.movies.slice(0, 5).map(m => m.title).join(', ')}
- Top Shows: ${profile.favorites.tvShows.slice(0, 5).map(s => s.title).join(', ')}
- Top Anime: ${profile.favorites.anime.map(a => a.title).join(', ')}
- Dislikes: ${profile.dislikes.franchises.join(', ')}
- Preferred Networks: ${profile.favorites.networks.join(', ')}
`.trim();
}
